import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';
import { useDispatch, useSelector } from 'react-redux';

import { getSmsDashboardConversationsKey } from './useConversations';
import { showResult } from '../utils';
import { useSmsDashboardUnreadCountKey } from './useUnreadConversationsCount';

import type {
  IConversation,
  IConversationsListPages,
  IUnreadConversationsCount,
} from 'infrastructure/interfaces';
import type { RootDispatch, RootState } from 'store';

interface Props {
  onSuccess?: () => void;
}

interface FnProps {
  conversationId: number;
  read: boolean;
}

const markConversationFn = async ({ conversationId, read }: FnProps) => {
  const API_MARK_READ_UNREAD = (id: number) =>
    `core/conversations/${id}/mark-read-unread`;

  const { data, error } = await dataService.createOne(
    API_MARK_READ_UNREAD(conversationId),
    {
      read,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useMarkConversation = (props: Props) => {
  const queryClient = useQueryClient();
  const { conversationType, filters, currentConversation } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const {
    smsDashboard: { changeCurrentConversation },
  } = useDispatch<RootDispatch>();

  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: markConversationFn,
    async onSuccess(data, variables) {
      const conversationKey = getSmsDashboardConversationsKey({
        type: conversationType,
        read: filters.read,
        closed: filters.closed,
        assignedToMe: filters.assignedToMe,
        unenrolled: filters.unenrolled,
        withoutAssignedCcmSpecialist: filters.withoutAssignedCcmSpecialist,
        lastMessageDate: filters.lastMessageDate,
        searchQuery: filters.searchQuery,
        rpmStatuses: filters.rpmStatuses,
        ccmStatuses: filters.ccmStatuses,
      });

      const conversations =
        queryClient.getQueryData<IConversationsListPages>(conversationKey);

      const pages = structuredClone(conversations?.pages ?? []);
      const [firstPage, ...restPages] = pages;

      pages.forEach((page) => {
        const index = page.items.findIndex(
          (item: IConversation) => item.id === variables.conversationId,
        );
        if (index !== -1) {
          page.items[index] = {
            ...page.items[index],
            read: variables.read,
            unreadMessageCount: 0,
            closed: false,
          };
        }
      });

      const updatedPages = [firstPage, ...restPages];

      await queryClient.setQueryData<IConversationsListPages>(
        conversationKey,
        (oldConversations) => {
          if (!oldConversations) return;
          return { ...oldConversations, pages: updatedPages };
        },
      );
      queryClient.setQueryData<IUnreadConversationsCount>(
        useSmsDashboardUnreadCountKey(),
        (old) => {
          if (!old) return;

          return {
            rpm: {
              ...old.rpm,
              unread: old.rpm.unread + data.rpmTotalUnreadCount,
            },
            ccm: {
              ...old.ccm,
              unread: old.ccm.unread + data.ccmTotalUnreadCount,
            },
            rpmUnregistered: {
              ...old.rpmUnregistered,
              unread:
                old.rpmUnregistered.unread +
                data.unregisteredRpmTotalUnreadCount,
            },
            ccmUnregistered: {
              ...old.ccmUnregistered,
              unread:
                old.ccmUnregistered.unread +
                data.unregisteredCcmTotalUnreadCount,
            },
          };
        },
      );
      if (currentConversation.id === variables.conversationId) {
        changeCurrentConversation({
          ...currentConversation,
          read: variables.read,
        });
      }
      if (props.onSuccess) {
        await props.onSuccess();
      }
    },
  });

  return {
    markConversation: mutate,
    markConversationLoading: isLoading,
    ...other,
  };
};
